/* eslint-disable no-unused-vars */
const pannellum = window.pannellum;
const THREE = window.THREE;

// Widest field of view (zoom-out limit) of the inspect viewer. Pannellum's
// rectilinear projection breaks down as hfov approaches 180°.
const INSPECT_MAX_HFOV = 120;

import { axisAngleToEuler } from "@/utils/geometryUtils";
import aIInspectService from "../service/aIInspect.service";
import images360Service from "../service/images360.service";
import { $uuidv4 } from "@/utils";
import HttpService from "@/utils/httpService";
import { getImageDimensionsAsync, pixelToYawPitch, resetInspectHotspots, updateInspectHotspots } from "../utils/panorama";
import { Image360 } from "@/scripts/js/forgeModels";
import Vue from "vue";
import analysisJobService from "../service/analysisJob.service";
import { $handleError, $updateArr, $updateOrAdd,$deleteFromArray } from '@/utils';
import { log, warn, error } from '@/utils/debugLogger';
import { computeNorthOffset } from '../utils/compass';
import { acceptedAnomalies, normalizeVerifiedAnomalyFilters, verifiedAnomalyQuery, verifiedAnomalyPage, avaInspectGroupPage, avaInspectGroupQuery, replaceGroupEntries } from '../utils/verifiedAnomalies';

const GROUP_LOAD_FAILED = 'Could not load the findings of this AVA Inspect tag. Please retry.';

/** One AVA Inspect group's accepted findings (its top-level rows; other views come nested as children). */
async function fetchGroupEntries(options, facilityId, avaInspectId) {
  const response = await aIInspectService.get(avaInspectGroupQuery(options, facilityId, avaInspectId), { timeout: 20000 });
  return { avaInspectId, items: verifiedAnomalyPage(response.data, facilityId).items };
}

export const AI_INSPECT = {
  namespaced: true,
  state: {
    // Findings of the loaded AVA Inspect groups (every row carries its avaInspectId).
    entries: [],
    images: [],
    // The page of AVA Inspect groups: [{ avaInspectId, avaInspectName, avaInspectTitle, masterTag, findingCount, assessment }].
    groups: [],
    expandedGroupIds: [],
    // avaInspectId -> { loading, error } while a group's findings load on expand.
    groupStatus: {},
    // Number of AVA Inspect groups matching the query (the table pages groups, not findings).
    total: 0,
    loading: false,
    facilityId: null,
    scopeEpoch: 0,
    requestId: 0,
    imagesRequestId: 0,
    filtersRequestId: 0,
    lastError: null,
    imagesError: null,
    imagesLoading: false,
    hasLoaded: false,
    updatesInFlight: 0,
    updateLoading: false,
    inviteLoading: false,
    options: {
      sortDesc: false,
      itemsPerPage: 10,
      page: 1,
      filters: normalizeVerifiedAnomalyFilters(),
      search: '',
    },
    filters: normalizeVerifiedAnomalyFilters(),
    inspectViewer: null,
    animationRequestId: null,
    isolatedInspects: [],
    currentInspect: null,
    viewerOpened: false,
    viewerTab: '360 View',
    isPanoramaViewerLoading: false,
    lastPanoramaPreviewImage: null,
    is360PreviewImageLoaded: false,
  },
  getters: {
    LAST_ERROR: (state) => state.lastError,
    IMAGES_ERROR: (state) => state.imagesError,
    HAS_LOADED: (state) => state.hasLoaded,
    GET_ENTRIES(state) {
      return state.entries;
    },
    GROUPS: (state) => state.groups,
    EXPANDED_GROUP_IDS: (state) => state.expandedGroupIds,
    GROUP_STATUS: (state) => state.groupStatus,
    FILTERS(state) {
      return state.filters;
    },
    IMAGES(state) {
      return state.images;
    },
    GET_TOTAL(state) {
      return state.total;
    },
    GET_LOADING(state) {
      return state.loading;
    },
    GET_UPDATE_LOADING(state) {
      return state.updateLoading;
    },
    GET_OPTIONS(state) {
      return state.options;
    },
    INSPECT_VIEWER(state) {
      return state.inspectViewer;
    },
    ANIMATION_REQUEST_ID(state) {
      return state.animationRequestId;
    },
    ISOLATED_INSPECTS(state) {
      return state.isolatedInspects;
    },
    CURRENT_INSPECT(state) {
      return state.currentInspect;
    },
    VIEWER_OPENED(state) {
      return state.viewerOpened;
    },
    VIEWER_TAB(state) {
      return state.viewerTab;
    },
    IS_PANORAMA_VIEWER_LOADING(state) {
      return state.isPanoramaViewerLoading;
    },
    LAST_PANORAMA_PREVIEW_IMAGE(state) {
      return state.lastPanoramaPreviewImage;
    },
    IS_360_PREVIEW_IMAGE_LOADED(state) {
      return state.is360PreviewImageLoaded
    }
  },
  mutations: {
    INVALIDATE_REQUESTS(state) {
      state.scopeEpoch++;
      state.requestId++;
      state.imagesRequestId++;
      state.filtersRequestId++;
      state.loading = false;
      state.imagesLoading = false;
      state.updatesInFlight = 0;
      state.updateLoading = false;
    },
    SET_FACILITY_ID(state, facilityId) {
      facilityId = facilityId || null;
      if (state.facilityId === facilityId) return;
      AI_INSPECT.mutations.INVALIDATE_REQUESTS(state);
      state.facilityId = facilityId;
      state.options = { ...state.options, facilityId, page: 1, search: '', filters: { ...normalizeVerifiedAnomalyFilters(), approvalStatus: ['Accepted'] } };
      state.entries = [];
      state.groups = [];
      state.expandedGroupIds = [];
      state.groupStatus = {};
      state.total = 0;
      state.images = [];
      state.filters = normalizeVerifiedAnomalyFilters();
      state.lastError = null;
      state.imagesError = null;
      state.hasLoaded = false;
      state.currentInspect = null;
      state.isolatedInspects = [];
      state.viewerOpened = false;
      state.lastPanoramaPreviewImage = null;
      state.is360PreviewImageLoaded = false;
    },
    BEGIN_REQUEST(state) { state.requestId++; state.loading = true; state.lastError = null; },
    INVALIDATE_ROWS(state) { state.requestId++; state.loading = false; },
    BEGIN_IMAGES_REQUEST(state) { state.imagesRequestId++; state.imagesLoading = true; state.imagesError = null; },
    BEGIN_FILTERS_REQUEST(state) { state.filtersRequestId++; },
    SET_LAST_ERROR(state, err) { state.lastError = err; },
    SET_IMAGES_ERROR(state, err) { state.imagesError = err; },
    SET_IMAGES_LOADING(state, loading) { state.imagesLoading = loading; },
    SET_HAS_LOADED(state, loaded) { state.hasLoaded = loaded; },
    BEGIN_UPDATE(state) { state.updatesInFlight++; state.updateLoading = true; },
    END_UPDATE(state) { state.updatesInFlight = Math.max(0, state.updatesInFlight - 1); state.updateLoading = state.updatesInFlight > 0; },
    REMOVE_ENTRY(state, id) {
      const removed = state.entries.find((entry) => entry.id === id);
      state.entries = state.entries.filter((entry) => entry.id !== id);
      if (!removed) return;
      // The table pages groups: a group left with no findings leaves the page.
      const groups = state.groups.map((group) => (group.avaInspectId === removed.avaInspectId
        ? { ...group, findingCount: Math.max(0, group.findingCount - 1) }
        : group));
      const remaining = groups.filter((group) => group.findingCount > 0);
      state.total = Math.max(0, state.total - (groups.length - remaining.length));
      state.groups = remaining;
    },
    SET_ENTRIES(state, entries) {
      state.entries = acceptedAnomalies(entries, state.facilityId);
    },
    SET_GROUPS(state, groups) {
      state.groups = Array.isArray(groups) ? groups : [];
    },
    SET_EXPANDED_GROUP_IDS(state, ids) {
      state.expandedGroupIds = Array.isArray(ids) ? [...ids] : [];
    },
    SET_GROUP_STATUS(state, { avaInspectId, loading, error }) {
      state.groupStatus = { ...state.groupStatus, [avaInspectId]: { loading: !!loading, error: error || null } };
    },
    SET_GROUP_ENTRIES(state, { avaInspectId, items }) {
      state.entries = acceptedAnomalies(replaceGroupEntries(state.entries, avaInspectId, items), state.facilityId);
    },
    SET_FILTERS(state, filters) {
      state.filters = normalizeVerifiedAnomalyFilters(filters);
    },
    SET_IMAGES(state, images) {
      state.images = images;
    },
    SET_TOTAL(state, total) {
      state.total = total;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_UPDATE_LOADING(state, loading) {
      state.updateLoading = loading;
    },
    SET_OPTIONS(state, options) {
      const previousQuery = JSON.stringify(verifiedAnomalyQuery(state.options, state.facilityId));
      if (Object.prototype.hasOwnProperty.call(options, 'facilityId')) {
        AI_INSPECT.mutations.SET_FACILITY_ID(state, options.facilityId);
      }
      state.options = { ...state.options, ...options, facilityId: state.facilityId,
        filters: { ...normalizeVerifiedAnomalyFilters(options.filters || state.options.filters), approvalStatus: ['Accepted'] } };
      if (previousQuery !== JSON.stringify(verifiedAnomalyQuery(state.options, state.facilityId))) {
        // Retained rows are valid only for the same query, including during the view's debounce.
        AI_INSPECT.mutations.INVALIDATE_ROWS(state);
        state.entries = [];
        state.groups = [];
        state.groupStatus = {};
        state.total = 0;
        state.hasLoaded = false;
        state.lastError = null;
      }
    },
    SET_INSPECT_VIEWER(state, inspectViewer) {
      state.inspectViewer = inspectViewer;
    },
    SET_ANIMATION_REQUEST_ID(state, requestId) {
      state.animationRequestId = requestId;
    },
    SET_ISOLATED_INSPECTS(state, inspects) {
      state.isolatedInspects = inspects;
    },
    SET_CURRENT_INSPECT(state, inspect) {
      state.currentInspect = inspect;
    },
    SET_VIEWER_OPENED(state, opened) {
      state.viewerOpened = opened;
    },
    SET_VIEWER_TAB(state, tab) {
      state.viewerTab = tab;
    },
    SET_IS_PANORAMA_VIEWER_LOADING(state, loading) {
      state.isPanoramaViewerLoading = loading;
    },
    SET_LAST_PANORAMA_PREVIEW_IMAGE(state, image) {
      state.lastPanoramaPreviewImage = image;
    },
    SET_IS_360_PREVIEW_IMAGE_LOADED(state, loaded) {
      state.is360PreviewImageLoaded = loaded
    },
    UPDATE_ENTRY(state, entry) {
      if (!entry?.id) return;
      const accepted = acceptedAnomalies([entry], state.facilityId)[0];
      if (!accepted) { AI_INSPECT.mutations.REMOVE_ENTRY(state, entry.id); return; }
      state.entries = state.entries.map((current) => current.id === entry.id ? accepted : current);
    },
    PATCH_POSITION(state, patch) {
      if (!patch?.id) return;
      state.entries = state.entries.map((entry) => entry.id === patch.id
        ? {
          ...entry,
          point3d: patch.point3d,
          updateDate: patch.updateDate,
          updatedById: patch.updatedById,
        }
        : entry);
    }
  },
  actions: {
    RESET({ commit }) {
      commit('INVALIDATE_REQUESTS');
      commit('SET_VIEWER_OPENED', false);
      commit('SET_VIEWER_TAB', '360 View');
      commit('SET_IS_PANORAMA_VIEWER_LOADING', false);
      commit('SET_LAST_PANORAMA_PREVIEW_IMAGE', null);
      commit('SET_IS_360_PREVIEW_IMAGE_LOADED', false);
    },
    /**
     * A page of AVA Inspect groups (AVA tag -> its master tag -> findings), plus the findings of the
     * groups that are open. Full scan findings are not shown on this page for now.
     */
    async GET({ commit, state }) {
      if (!state.facilityId) return null;
      commit('BEGIN_REQUEST');
      const requestId = state.requestId, facilityId = state.facilityId;
      const isCurrent = () => requestId === state.requestId && facilityId === state.facilityId;
      try {
        const response = await aIInspectService.getAvaInspectGroups(verifiedAnomalyQuery(state.options, facilityId), { timeout: 20000 });
        if (!isCurrent()) return null;
        const result = avaInspectGroupPage(response.data);
        const ids = result.items.map((group) => group.avaInspectId);
        // Open groups stay open across refreshes; a page with a single group opens it.
        const expanded = ids.length === 1 ? ids : state.expandedGroupIds.filter((id) => ids.includes(id));
        const loaded = await Promise.all(expanded.map((id) => fetchGroupEntries(state.options, facilityId, id)));
        if (!isCurrent()) return null;
        commit('SET_GROUPS', result.items);
        commit('SET_EXPANDED_GROUP_IDS', expanded);
        commit('SET_ENTRIES', loaded.reduce((entries, group) => replaceGroupEntries(entries, group.avaInspectId, group.items), []));
        commit('SET_TOTAL', result.total);
        commit('SET_HAS_LOADED', true);
        return result;
      } catch (err) {
        if (!isCurrent()) return null;
        commit('SET_LAST_ERROR', err.message || 'Unable to load verified anomalies. Please retry.');
        throw err;
      } finally {
        if (isCurrent()) commit('SET_LOADING', false);
      }
    },
    /** Opens or closes an AVA Inspect group; its findings load the first time it opens. */
    async TOGGLE_GROUP({ commit, dispatch, state }, avaInspectId) {
      const isOpen = state.expandedGroupIds.includes(avaInspectId);
      commit('SET_EXPANDED_GROUP_IDS', isOpen
        ? state.expandedGroupIds.filter((id) => id !== avaInspectId)
        : [...state.expandedGroupIds, avaInspectId]);
      if (isOpen || state.entries.some((entry) => entry.avaInspectId === avaInspectId)) return;
      await dispatch('LOAD_GROUP', avaInspectId);
    },
    /** (Re)loads one AVA Inspect group's findings, e.g. after a failed expand. */
    async LOAD_GROUP({ commit, state }, avaInspectId) {
      const requestId = state.requestId, facilityId = state.facilityId;
      const isCurrent = () => requestId === state.requestId && facilityId === state.facilityId;
      commit('SET_GROUP_STATUS', { avaInspectId, loading: true });
      try {
        const { items } = await fetchGroupEntries(state.options, facilityId, avaInspectId);
        if (!isCurrent()) return;
        commit('SET_GROUP_ENTRIES', { avaInspectId, items });
        commit('SET_GROUP_STATUS', { avaInspectId, loading: false });
      } catch (err) {
        if (!isCurrent()) return;
        error('[AI_INSPECT] loading an AVA Inspect group failed', err);
        commit('SET_GROUP_STATUS', { avaInspectId, loading: false, error: GROUP_LOAD_FAILED });
      }
    },
    async GET_FILTERS({ commit, state }, payload = {}) {
      if (!state.facilityId) return null;
      commit('BEGIN_FILTERS_REQUEST');
      const requestId = state.filtersRequestId, facilityId = state.facilityId;
      try {
        const response = await aIInspectService.getFilters({ ...payload, facilityId, 'Filters.ApprovalStatus': ['Accepted'] }, { timeout: 20000 });
        if (requestId !== state.filtersRequestId || facilityId !== state.facilityId) return null;
        const filters = normalizeVerifiedAnomalyFilters(response.data);
        commit('SET_FILTERS', filters);
        return filters;
      } catch (err) {
        if (requestId !== state.filtersRequestId || facilityId !== state.facilityId) return null;
        throw err;
      }
    },
    GENERATE_REPORT({ commit, getters }, payload) {
      return new Promise((resolve, reject) => {
        aIInspectService
          .generateReport(payload.id)
          .then((response) => {
            resolve(response?.data)
          })
          .catch((error) => {
            log(error);
            reject(error)
          });
      });

    },
    async GET_IMAGES({ commit, state }) {
      // Older shared viewers still set options.facilityId directly.
      if (state.options.facilityId !== state.facilityId) commit('SET_FACILITY_ID', state.options.facilityId);
      if (!state.facilityId) return null;
      commit('BEGIN_IMAGES_REQUEST');
      const requestId = state.imagesRequestId, facilityId = state.facilityId;
      const isCurrent = () => requestId === state.imagesRequestId && facilityId === state.facilityId;
      try {
        const { data } = await images360Service.get({ facilityId }, { timeout: 20000 });
        if (!isCurrent()) return null;
        const images = data.map((value) => {
          const image = Object.assign(new Image360(), value);
          image.forgeCameraPosition = Object.assign(new THREE.Vector3(), image.forgeCameraPosition);
          image.width ??= 4057;
          image.height ??= 2028;
          return image;
        });
        commit('SET_IMAGES', images);
        return images;
      } catch (err) {
        if (!isCurrent()) return null;
        commit('SET_IMAGES_ERROR', err.message || 'Unable to load scan images.');
        throw err;
      } finally {
        if (isCurrent()) commit('SET_IMAGES_LOADING', false);
      }
    },
    async GET_OPTIONS({ commit, state }) {
      const epoch = state.scopeEpoch;
      const response = await aIInspectService.getOptions();
      if (epoch !== state.scopeEpoch) return null;
      commit('SET_OPTIONS', response.data);
      return response.data;
    },
    async UPDATE({ commit, state }, payload) {
      commit('BEGIN_UPDATE');
      const epoch = state.scopeEpoch;
      try {
        const response = await aIInspectService.update(payload);
        if (epoch !== state.scopeEpoch) return null;
        commit('INVALIDATE_ROWS');
        commit('UPDATE_ENTRY', response.data);
        return response.data;
      } finally {
        if (epoch === state.scopeEpoch) commit('END_UPDATE');
      }
    },
    async LINK_COMPONENT({ commit, state }, { id, masterTagId }) {
      commit('BEGIN_UPDATE');
      const epoch = state.scopeEpoch, facilityId = state.facilityId;
      try {
        const response = await aIInspectService.updateComponent(id, { masterTagId });
        if (epoch !== state.scopeEpoch || facilityId !== state.facilityId) return null;
        commit('INVALIDATE_ROWS');
        commit('UPDATE_ENTRY', response.data);
        return response.data;
      } finally {
        if (epoch === state.scopeEpoch && facilityId === state.facilityId) commit('END_UPDATE');
      }
    },
    async UPDATE_POSITION({ commit, state }, { id, imageId, point3d, expectedUpdateDate }) {
      commit('BEGIN_UPDATE');
      const epoch = state.scopeEpoch, facilityId = state.facilityId;
      const isCurrent = () => epoch === state.scopeEpoch && facilityId === state.facilityId;
      try {
        const response = await aIInspectService.updatePosition(id, { imageId, point3d, expectedUpdateDate });
        if (!isCurrent()) return null;
        commit('INVALIDATE_ROWS');
        commit('PATCH_POSITION', response.data);
        return response.data;
      } catch (err) {
        if (!isCurrent()) return null;
        throw err;
      } finally {
        if (isCurrent()) commit('END_UPDATE');
      }
    },
    async UPDATE_MULTIPLE({ commit, dispatch, state }, payload) {
      commit('BEGIN_UPDATE');
      const epoch = state.scopeEpoch;
      try {
        const response = await aIInspectService.updateMultiple(payload);
        if (epoch !== state.scopeEpoch) return null;
        await dispatch('GET');
        return response.data;
      } finally {
        if (epoch === state.scopeEpoch) commit('END_UPDATE');
      }
    },
    async DELETE({ commit, state }, id) {
      const epoch = state.scopeEpoch;
      await aIInspectService.delete(id);
      if (epoch === state.scopeEpoch) { commit('INVALIDATE_ROWS'); commit('REMOVE_ENTRY', id); }
    },
    async DELETE_MULTI({ dispatch, state }, payload) {
      const epoch = state.scopeEpoch;
      await aIInspectService.deleteMultiple(payload);
      if (epoch === state.scopeEpoch) return dispatch('GET');
      return null;
    },
    async ACCEPT({ dispatch, state }, id) {
      const epoch = state.scopeEpoch;
      await aIInspectService.accept(id);
      if (epoch === state.scopeEpoch) return dispatch('GET');
      return null;
    },
    async REJECT({ commit, state }, id) {
      const epoch = state.scopeEpoch;
      await aIInspectService.reject(id);
      if (epoch === state.scopeEpoch) { commit('INVALIDATE_ROWS'); commit('REMOVE_ENTRY', id); }
    },
    ADD_HOT_SPOT({ getters }, { pitch, yaw, text, cssClass }) {
      if (!getters.INSPECT_VIEWER) return;

      let id = $uuidv4();

      getters.INSPECT_VIEWER.addHotSpot({
        pitch: pitch,
        yaw: yaw,
        cssClass: cssClass || '',
        text: text,
        id: id,
      });

      return id;
    },
    LAUNCH_INSPECT_VIEWER({ commit, getters, dispatch }, { image, inspects }) {
      if (getters.IS_PANORAMA_VIEWER_LOADING) {
        Vue.prototype.$dialog.notify.error('Panorama Viewer is still loading. Please wait for a moment.');
        return;
      }

      let panoramaElement = document.querySelector('#inspect-viewer.pnlm-container');

      if (panoramaElement && getters.INSPECT_VIEWER && getters.INSPECT_VIEWER.forgeImage.id == image.id) {
        dispatch('LAUNCH_INSPECT_VIEWER_HANDLE', { image, inspects });
      } else {
        commit('SET_IS_PANORAMA_VIEWER_LOADING', true);
  
        if (getters.INSPECT_VIEWER) {
          var imageDataUrl = getters.INSPECT_VIEWER.getRenderer().render(getters.INSPECT_VIEWER.getPitch() / 180 * Math.PI, getters.INSPECT_VIEWER.getYaw() / 180 * Math.PI, getters.INSPECT_VIEWER.getHfov() / 180 * Math.PI, {'returnImage': true});
          var img = new Image();
          
          img.onload = () => {
            var canvas = document.createElement('canvas');
            var ctx = canvas.getContext('2d');
            canvas.width = img.width / 6;
            canvas.height = img.height / 6;
            ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, canvas.width, canvas.height);
            commit('SET_LAST_PANORAMA_PREVIEW_IMAGE', canvas.toDataURL());
            commit('SET_IS_360_PREVIEW_IMAGE_LOADED', false);
          }
    
          img.src = imageDataUrl;
        }
    
        setTimeout(() => {
          dispatch('LAUNCH_INSPECT_VIEWER_HANDLE', { image, inspects });
        }, 1000)
      }
    },
    async LAUNCH_INSPECT_VIEWER_HANDLE({ commit, getters, dispatch }, { image, inspects }) {
      if (!image.imageUrl) {
        Vue.prototype.$dialog.notify.warning('No image found.');
        return;
      }

      const onLoad = () => {
        if (getters.ANIMATION_REQUEST_ID)
          cancelAnimationFrame(getters.ANIMATION_REQUEST_ID);

        if (!inspects?.length) return;

        const fps = 60;
        let lastRender = 0;

        function animate(timestamp) {
          const progress = timestamp - lastRender;

          if (progress > 1000 / fps) {
            lastRender = timestamp;
            updateInspectHotspots(getters.INSPECT_VIEWER, image.width, image.height, inspects);
          }

          let requestId = requestAnimationFrame(animate);
          commit('SET_ANIMATION_REQUEST_ID', requestId);
        }

        setTimeout(() => {
          requestAnimationFrame(animate);
        }, 1000);

        let yawPitch = pixelToYawPitch(inspects[0].centerPoint.x, inspects[0].centerPoint.y, image.width, image.height);

        getters.INSPECT_VIEWER.setYaw(yawPitch.yaw);
        getters.INSPECT_VIEWER.setPitch(yawPitch.pitch);

        const intervalId = setInterval(() => {
          // if (!getters.INSPECT_VIEWER?.getRenderer()?.isLoading()) {
          if (getters.INSPECT_VIEWER?.isLoaded()) {
            commit('SET_IS_PANORAMA_VIEWER_LOADING', false);
            clearInterval(intervalId);
          }
        }, 2000);
      };

      // Fetch signed URLs BEFORE the Promise (async executor is an anti-pattern)
      let signedTilesBaseUrl = image.tilesBaseUrl;
      let sasToken = '';

      if (image.tilesGenerated && image.highResolution && signedTilesBaseUrl &&
          (signedTilesBaseUrl.includes('blob.core.windows.net') || signedTilesBaseUrl.includes('127.0.0.1:10000'))) {
        try {
          const response = await HttpService.get({
            url: '/api/BlobStorage/sign360TilesFolder',
            params: { tilesBaseUrl: signedTilesBaseUrl }
          });
          if (response.data) {
            signedTilesBaseUrl = response.data.baseUrl;
            sasToken = response.data.sasToken || '';
          }
        } catch (err) {
          warn('Failed to sign 360 tiles URL, using original:', err);
        }
      }

      return new Promise((resolve) => {
        let panoramaElement = document.querySelector('#inspect-viewer.pnlm-container');

        if (panoramaElement && getters.INSPECT_VIEWER && getters.INSPECT_VIEWER.forgeImage.id == image.id) {
          resetInspectHotspots();
          onLoad();
          resolve();
          return;
        }

        if (getters.INSPECT_VIEWER) {
          resetInspectHotspots();
          getters.INSPECT_VIEWER.destroy();
        }

        if (!image.rotationAngle) image.rotationAngle = Math.abs(image.lockedYaw);

        let horizonRoll, horizonPitch = 0;

        if (image.rotationAngle && image.rotationX && image.rotationY && image.rotationZ) {
          var rotation = axisAngleToEuler(
            image.rotationX,
            image.rotationY,
            image.rotationZ,
            image.rotationAngle,
          );
          horizonPitch = rotation.y;
          horizonRoll = -rotation.x;
        } else if (image.scanType == 'geoslam') {
          horizonRoll = image.rotationX;
          horizonPitch = image.rotationY;
        }

        let url = image.lowResUrl??image.imageUrl ;
        let inspectViewer = {};
        if (image.tilesGenerated && image.highResolution) {
          var config=JSON.parse(image.multiResConfig)?.multiRes??  {
              "basePath":image.tilesBaseUrl,
              "path": "/%l/%s%y_%x",
              "fallbackPath": "/fallback/%s",
              "extension": "jpg",
              "tileResolution": 512,
              "maxLevel": 5,
              "cubeResolution": 6456
            };

          // Use pre-fetched signed URL and SAS token (fetched before Promise)
          config.basePath = signedTilesBaseUrl;

          // Append SAS token to extension so Pannellum constructs URLs correctly:
          // basePath + path + '.' + extension => .../1/f0_0.jpg?sv=...&sig=...
          if (sasToken) {
            config.extension = `${config.extension || 'jpg'}?${sasToken}`;
          }

          inspectViewer = pannellum.viewer('inspect-viewer', {
            type: 'multires',
            autoLoad: true,
            multiRes: config,
            yaw: image.lockedYaw,
            pitch: image.lockedPitch,
            horizonPitch: horizonPitch,
            horizonRoll: horizonRoll,
            // Built-in compass disabled — PanoramaCompass Vue overlay renders
            // our own compass wherever a .pnlm-container is mounted in the DOM.
            compass: false,
            northOffset: computeNorthOffset(image.lockedYaw, image.lockedForgeDirection),
            sceneFadeDuration: 1000,
            hfov: 50,
            minHfov: 10,
            maxHfov: INSPECT_MAX_HFOV,
            scenes: {
              customScene: {
                type: 'multires',
                autoLoad: true,
                multiRes: config,
              },
            },
          });
        }
        else {
          inspectViewer = pannellum.viewer('inspect-viewer', {
            type: 'equirectangular',
            panorama: url,
            autoLoad: true,
            yaw: image.lockedYaw,
            pitch: image.lockedPitch,
            horizonPitch: horizonPitch,
            horizonRoll: horizonRoll,
            // Built-in compass disabled — see multires branch above.
            compass: false,
            northOffset: computeNorthOffset(image.lockedYaw, image.lockedForgeDirection),
            sceneFadeDuration: 1000,
            hfov: 50,
            minHfov: 10,
            maxHfov: INSPECT_MAX_HFOV,
            scenes: {
              customScene: {
                type: 'equirectangular',
                panorama: url,
                autoLoad: true,
              },
            },
          });
        }

        commit('SET_INSPECT_VIEWER', inspectViewer);

        getters.INSPECT_VIEWER.loadScene(
          this.panoCustomSceneId,
          image.lockedPitch,
          image.lockedYaw,
          getters.INSPECT_VIEWER.getHfov(),
        );

        getters.INSPECT_VIEWER.forgeImage = image;

        let onPointerDown = (event) => {
          if (event.button != 0) return;
        };

        let onPointerUp = (event) => {
          if (event.button != 0) return;
        };

        getters.INSPECT_VIEWER.on('touchstart', onPointerDown);
        getters.INSPECT_VIEWER.on('touchend', onPointerUp);
        getters.INSPECT_VIEWER.on('mousedown', onPointerDown);
        getters.INSPECT_VIEWER.on('mouseup', onPointerUp);
        getters.INSPECT_VIEWER.on('load', onLoad);
      });

    },
    SHOW_INSPECTS_IN_VIEWER({ dispatch, getters, commit }, inspect) {
      if (!inspect) return;

      commit('SET_CURRENT_INSPECT', inspect);

      let index = getters.IMAGES.findIndex(o => o.id == inspect.imageId);

      if (index == -1) return;

      let image = getters.IMAGES[index];

      const inspectItems = [
        {
          imageWidth: inspect.imageWidth ?? 75,
          imageHeight: inspect.imageHeight ?? 100,
          centerPoint: {
            x: parseInt(inspect.imageXCenterCoordinate),
            y: parseInt(inspect.imageYCenterCoordinate),
          }
        }
        // {
        //   imageUrl: 'http://127.0.0.1:10000/devstoreaccount1/reality/ai/img-7c7b5279-ea68-4b7d-a35f-88c688060378.png',
        //   maskUrl: 'http://127.0.0.1:10000/devstoreaccount1/reality/ai/msk-7c7b5279-ea68-4b7d-a35f-88c688060378.png',
        //   imageWidth: 75,
        //   imageHeight: 100,
        //   centerPoint: { x: 2763, y: 791 }
        // },
      ];

      dispatch('LAUNCH_INSPECT_VIEWER', { image, inspects: inspectItems });
    },
    CLOSE_INSPECT_VIEWER({ commit, getters }) {
      if (getters.INSPECT_VIEWER) getters.INSPECT_VIEWER.destroy();
      commit('SET_INSPECT_VIEWER', null);
    },
    GET_PROCESSING_COUNT({ commit }, facilityId) {
      return aIInspectService
        .getProcessingCount(facilityId)
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          error(error);
        });
    },
    START_ANALYSIS_JOB({ dispatch }, { imagesIds, facilityId }) {
      return analysisJobService
        .startAnalysisJob({
          imagesIds,
          facilityId
        })
        .then(() => {
          Vue.prototype.$dialog.notify.success('Analysis Job has been successfully started');
        })
        .catch((error) => {
          Vue.prototype.$dialog.notify.error('Failed to start Analysis Job');
          error(error.message);
        });
    }
  }
}
